/**
 * Pure helpers for the grouped marine-incident feed card. A
 * `marine_incident_run` root ("Marine incidents — N new reports") expands to
 * one `marine_incident` child per vessel casualty; each child carries the
 * vessel, casualty type, location, and an external source link. These helpers
 * parse and derive what the drill-down renders, kept separate from the
 * component so they can be unit-tested without a DOM.
 */
import type {
  MarineIncidentPayload,
  MarineIncidentRunPayload,
  MarineIncidentSeverityTier,
} from "@/types/feed";

/** Whether a payload is a grouped marine-incident run root. */
export function isMarineIncidentRunPayload(
  payload: unknown,
): payload is MarineIncidentRunPayload {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  return p.kind === "marine_incident_run" && Array.isArray(p.incidents);
}

/** Whether a payload is a per-incident child. */
export function isMarineIncidentPayload(
  payload: unknown,
): payload is MarineIncidentPayload {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  return p.kind === "marine_incident" && typeof p.source_id === "string";
}

/** Human-readable label for a severity tier. */
export function marineIncidentSeverityLabel(
  tier: MarineIncidentSeverityTier,
): string {
  switch (tier) {
    case "critical":
      return "Critical";
    case "warning":
      return "Serious";
    case "info":
      return "Minor";
    case "neutral":
      return "Unclassified";
  }
}

/** Human-readable label for a coarse vessel-class token (e.g. `bulk_container`). */
export function marineVesselClassLabel(token: string): string {
  const trimmed = token.trim();
  if (!trimmed) return "";
  return trimmed
    .split("_")
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(" ");
}
